import type {
  SentryBoundaryLevel,
  SentryCrashOrigin,
  SentryCrashType,
  SentryModule,
} from '../types';

export const BOUNDARY_LEVEL = {
  APP: 'app',
  CONTAINER: 'container',
} as const satisfies Record<string, SentryBoundaryLevel>;

export const DEFAULT_BOUNDARY_LEVEL: SentryBoundaryLevel = BOUNDARY_LEVEL.CONTAINER;

export const CRASH_ORIGIN = {
  LOCAL: 'local',
  FEDERATED: 'federated',
  API: 'api',
  CHUNK: 'chunk',
  UNKNOWN: 'unknown',
} as const satisfies Record<string, SentryCrashOrigin>;

export const BOUNDARY_CRASH_TYPE = {
  APP_SHELL: 'app_shell_crash',
  CONTAINER_SECTION: 'container_section_crash',
} as const satisfies Record<string, SentryCrashType>;

export const BOUNDARY_LABEL = {
  APP: 'app_error_boundary',
  container: (scope: string): string => `container_crash:${scope}`,
} as const;

export const BOUNDARY_ERROR_CONFIG = {
  product: 'PG',
  team: 'FP',
} as const;

export const ERROR_BOUNDARY_METADATA = {
  modal: {
    paymentsRefund: {
      module: 'payments' as SentryModule,
      feature: 'refund_modal',
    },
    paymentLinksCreate: {
      module: 'payment_links' as SentryModule,
      feature: 'create_link',
    },
    paymentLinksBatchUpload: {
      module: 'payment_links' as SentryModule,
      feature: 'batch_upload',
    },
  },
  summary: {
    charts: {
      module: 'summary' as SentryModule,
      feature: 'summary_charts',
    },
    recentSettlements: {
      module: 'settlements' as SentryModule,
      feature: 'summary_recent_settlements',
      crashOrigin: CRASH_ORIGIN.LOCAL,
    },
    openDisputes: {
      module: 'disputes' as SentryModule,
      feature: 'summary_open_disputes',
    },
    riskShield: {
      module: 'risk_shield' as SentryModule,
      feature: 'summary_riskshield',
      crashOrigin: CRASH_ORIGIN.LOCAL,
    },
    pendingPaymentVerification: {
      module: 'payments' as SentryModule,
      feature: 'summary_pending_payment_verification',
    },
    suspicious: {
      module: 'payments' as SentryModule,
      feature: 'summary_suspicious',
    },
  },
  analytics: {
    businessTrends: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_business_trends',
    },
    conversionRate: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_conversion_rate',
    },
    paymentFailure: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_payment_failure',
    },
    srOverview: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_sr_overview',
    },
  },
  // Home / Insights: clear product owners stay; otherwise default to pg_app (PG).
  // Do not use cpss here — CPSS owns backend APIs, not dashboard frontend.
  insights: {
    businessMetrics: {
      module: 'pg_app' as SentryModule,
      feature: 'insights_business_metrics',
      componentName: 'Business Performance',
    },
    paymentMetrics: {
      module: 'payments' as SentryModule,
      feature: 'insights_payment_metrics',
      componentName: 'Payment Performance',
    },
    conversionMetrics: {
      module: 'pg_app' as SentryModule,
      feature: 'insights_conversion_metrics',
      componentName: 'Conversion and Checkout',
    },
    // Business Performance cards → PG (frontend default)
    grossSales: {
      module: 'pg_app' as SentryModule,
      feature: 'gross_sales',
      componentName: 'Gross Sales',
    },
    refunds: {
      module: 'pg_app' as SentryModule,
      feature: 'refunds',
      componentName: 'Refunds',
    },
    orders: {
      module: 'pg_app' as SentryModule,
      feature: 'orders',
      componentName: 'Orders',
    },
    regionalPerformance: {
      module: 'pg_app' as SentryModule,
      feature: 'regional_performance',
      componentName: 'Regional Performance',
    },
    paymentModes: {
      module: 'pg_app' as SentryModule,
      feature: 'preferred_payment_modes',
      componentName: 'Preferred Payment Modes',
    },
    customerDistribution: {
      module: 'pg_app' as SentryModule,
      feature: 'customers',
      componentName: 'Customers',
    },
    // Payment Performance cards
    overallSuccessRate: {
      module: 'payments' as SentryModule,
      feature: 'overall_success_rate',
      componentName: 'Overall Success Rate',
    },
    paymentModeSuccessRate: {
      module: 'payments' as SentryModule,
      feature: 'sr_by_payment_mode',
      componentName: 'SR by Payment Mode',
    },
    paymentMethodSplit: {
      module: 'payments' as SentryModule,
      feature: 'payment_method_split',
      componentName: 'Payment Method Split',
    },
    failureAnalysis: {
      module: 'payments' as SentryModule,
      feature: 'failure_analysis',
      componentName: 'Failure Analysis',
    },
    // Conversion and Checkout cards → PG
    checkoutFunnel: {
      module: 'pg_app' as SentryModule,
      feature: 'checkout_funnel',
      componentName: 'Checkout Funnel',
    },
    conversionRate: {
      module: 'pg_app' as SentryModule,
      feature: 'checkout_conversion_trend',
      componentName: 'Checkout Conversion Trend',
    },
    couponDrivenOrders: {
      module: 'pg_app' as SentryModule,
      feature: 'coupon_driven_orders',
      componentName: 'Coupon-Driven Orders',
    },
    newOffers: {
      module: 'pg_app' as SentryModule,
      feature: 'new_offers',
      componentName: 'New Offers',
    },
    topOffers: {
      module: 'pg_app' as SentryModule,
      feature: 'top_offers',
      componentName: 'Top Offers',
    },
    failedOffers: {
      module: 'pg_app' as SentryModule,
      feature: 'failed_offers',
      componentName: 'Failed Offers',
    },
    offerSummary: {
      module: 'pg_app' as SentryModule,
      feature: 'offer_summary',
      componentName: 'Offer Summary',
    },
  },
  home: {
    totalEarned: {
      module: 'pg_app' as SentryModule,
      feature: 'home_total_earned',
      componentName: 'Total Earned',
    },
    lastSettled: {
      module: 'settlements' as SentryModule,
      feature: 'home_last_settled',
      componentName: 'Last Settled',
    },
    openDisputes: {
      module: 'disputes' as SentryModule,
      feature: 'home_open_disputes',
      componentName: 'Open Disputes',
    },
    recentTransactions: {
      module: 'payments' as SentryModule,
      feature: 'home_recent_transactions',
      componentName: 'Recent Transactions',
    },
  },
  federated: {
    manualApproval: {
      module: 'payments' as SentryModule,
      feature: 'manual_approval',
      remoteName: 'MANUAL_APPROVAL',
    },
    addToRiskshield: {
      module: 'risk_shield' as SentryModule,
      feature: 'add_to_blacklist',
      remoteName: 'ADD_TO_RISKSHIELD',
    },
    riskShieldRemote: {
      module: 'risk_shield' as SentryModule,
      feature: 'riskshield_remote',
      remoteName: 'RISKSHIELD',
    },
    riskMetricsRemote: {
      module: 'risk_shield' as SentryModule,
      feature: 'insights_risk_metrics',
      componentName: 'Risk Metrics',
      remoteName: 'RISK_METRICS',
    },
    helpCenterForm: {
      module: 'payment_forms' as SentryModule,
      feature: 'help_center_form',
      remoteName: 'PAYMENT_FORMS',
    },
    notificationRemote: {
      feature: 'common_notifications',
      remoteName: 'COMMON_NOTIFICATIONS',
    },
  },
} as const;